import { useEffect, useMemo, useRef } from 'react';

import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import { useReadOnlyContext } from '~/common/hooks/useReadOnly';
import useUrl from '~/common/hooks/useUrl';
import { useModalState } from '~/modals/modalStore';
import { useLoanLockInformation } from '~/stores/navigationStore';

const tabMessageTypes = {
  OPEN: 'open',
  CREATED_AT: 'createdAt',
  CLOSED: 'closed',
} as const;

type TabMessage = {
  type: (typeof tabMessageTypes)[keyof typeof tabMessageTypes];
  payload: {
    tabCreatedAt: number;
    fromReadOnlyOrAuditHistory: boolean;
  };
};

const isReadOnlyOrAuditHistory = () => {
  const path = globalThis.location.pathname.toLowerCase();
  const search = globalThis.location.search.toLowerCase();
  return (
    path.includes('readonly') ||
    path.includes('audit-history') ||
    search.includes('showbothtables=1')
  );
};

/*** 
 This hook manages setting bolt to read only by making use of the BroadcastChannel API.
 It listens for messages from other tabs and updates the read-only state based on whichever tabs created date is the oldest.
 It also handles the case where a loan is locked by a user, setting the read-only state when a different user has a lock on the loan in edge.
 The lock on the loan in edge takes precendence over the tab lock.
 ***/
export function useLoanTabLock() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { onOpen: showModal } = useModalState('loanLock');
  const channelRef = useRef<BroadcastChannel | null>(null);
  const localTabCreatedAt = useMemo(() => Date.now(), []);
  const allTabCreatedAtTimesRef = useRef(new Set([localTabCreatedAt]));
  const shouldHandleMessageRef = useRef<boolean>(true);
  const { setIsReadOnly, isReadOnly } = useReadOnlyContext();
  const { IsBoltCurrentlyLockedByUserEnabled } = useNavFeatureFlags();
  const loanLockedByUser = useLoanLockInformation();

  const createMessage = (type: string) => ({
    type,
    payload: {
      tabCreatedAt: localTabCreatedAt,
      fromReadOnlyOrAuditHistory: isReadOnlyOrAuditHistory(),
    },
  });

  const evaluateReadOnly = () => {
    const tabCreatedAtToCompare = Math.min(...allTabCreatedAtTimesRef.current);

    if (tabCreatedAtToCompare < localTabCreatedAt) {
      showModal();
      setIsReadOnly(true);
    } else {
      setIsReadOnly(false);
    }
  };

  const handleOnMessage = (event: MessageEvent) => {
    const { type, payload } = event.data as TabMessage;
    const { tabCreatedAt, fromReadOnlyOrAuditHistory } = payload;

    const shouldNotHandleMessage =
      fromReadOnlyOrAuditHistory || !shouldHandleMessageRef.current;

    if (shouldNotHandleMessage) return;

    switch (type) {
      case tabMessageTypes.OPEN:
        allTabCreatedAtTimesRef.current.add(tabCreatedAt);

        channelRef.current?.postMessage(
          createMessage(tabMessageTypes.CREATED_AT),
        );
        break;

      case tabMessageTypes.CREATED_AT:
        if (allTabCreatedAtTimesRef.current.has(tabCreatedAt)) return;

        allTabCreatedAtTimesRef.current.add(tabCreatedAt);
        evaluateReadOnly();
        break;

      case tabMessageTypes.CLOSED:
        allTabCreatedAtTimesRef.current.delete(tabCreatedAt);
        allTabCreatedAtTimesRef.current.size === 1
          ? setIsReadOnly(false)
          : evaluateReadOnly();
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    channelRef.current = new BroadcastChannel(
      obfuscatedLoanIdentifier?.obfuscatedLoanRecordId.toString(),
    );

    if (!isReadOnlyOrAuditHistory()) {
      channelRef.current.onmessage = handleOnMessage;
    }

    const channel = channelRef.current;

    const cleanUpChannel = () => {
      channel.onmessage = null;

      try {
        channel.postMessage(createMessage(tabMessageTypes.CLOSED));
      } catch {
        // Channel may already be closed by the browser during page unload
      }

      channel.close();
    };

    window.addEventListener('beforeunload', cleanUpChannel);

    return () => {
      window.removeEventListener('beforeunload', cleanUpChannel);
      cleanUpChannel();
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReadOnlyOrAuditHistory()) return;

    const { loanLockedBy, isLoanCurrentlyLocked } = loanLockedByUser;
    const isLoanLockedByUser = isLoanCurrentlyLocked && !!loanLockedBy?.trim();

    if (isLoanLockedByUser) {
      shouldHandleMessageRef.current = false;
      showModal();
      setIsReadOnly(true);
    } else {
      shouldHandleMessageRef.current = IsBoltCurrentlyLockedByUserEnabled;

      if (IsBoltCurrentlyLockedByUserEnabled && !isReadOnly) {
        channelRef.current?.postMessage(createMessage(tabMessageTypes.OPEN));
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [loanLockedByUser, IsBoltCurrentlyLockedByUserEnabled, isReadOnly]);
}
